import { useTranslation } from 'next-i18next';
import { ComponentProps, FormEvent, useState } from 'react';
import { Box, Rating } from '@bridebook/ui';
import BorderedContainer from '@bridebook/web/app-shared/components/supplier/sections/bordered-container/bordered-container';
import { IFeedbackSerialized } from '@bridebook/web/app-shared/lib/supplier/types';
import { cleanReviewsDate } from '@bridebook/web/app-shared/lib/supplier/utils/clean-reviews-date';
import { format } from '@bridebook/web/lib/i18n/utils/get-date-fns-format';
import { getFormattedDate } from '@bridebook/web/lib/utils';
import Message, { IProps as MessageProps } from './message';
import ReviewImage from './review-image';
import componentStyles from './review.style';

interface IProps {
  endorser: Partial<IFeedbackSerialized>;
  onReadMore?(): void;
  isMobile: boolean;
  reviewImageProps: Omit<ComponentProps<typeof ReviewImage>, 'photo'>;
  messageProps: Pick<MessageProps, 'trimText' | 'isDesktop' | 'isVenue'>;
}

const Endorser = ({
  endorser,
  endorser: { title, name, from, stars = 0 },
  onReadMore,
  reviewImageProps,
  messageProps,
}: IProps) => {
  const { t } = useTranslation('supplier');
  const [more, setMore] = useState(false);
  const photo = endorser.photos?.[0];
  const message = endorser.body;
  const createdAt = endorser.createdAt;
  const isReview = endorser.type === 'review';
  const response = endorser.response;
  const isHappyCouple = from === 'happyCouple';
  const datePublished = createdAt ? new Date(createdAt) : '';

  const readMore = (e: FormEvent) => {
    e.preventDefault();

    if (more) {
      onReadMore?.();
    }

    setMore(!more);
  };

  const styles = componentStyles();
  const { dateWedding } = endorser;

  const date = typeof dateWedding === 'string' ? cleanReviewsDate(dateWedding) : dateWedding;
  const weddingDate = date ? format(new Date(date), 'MMMM') : undefined;
  return (
    <BorderedContainer>
      <Box style={styles.wrapper}>
        <Box style={styles.leftContainer}>
          <Box style={styles.topContainer}>
            <Box justifyContent="center">
              <Box style={styles.name}>{name}</Box>
              {date && (
                <Box style={styles.who}>
                  {isHappyCouple
                    ? t('review.card.subtext', {
                        weddingDate,
                      })
                    : t('supplier.review.date.wedding', { weddingDate })}
                </Box>
              )}
            </Box>
          </Box>

          <Box style={styles.stats}>
            <Box style={styles.typeWrap}>
              <Box style={styles.rating}>
                <Rating rating={isReview ? stars : 5} />
              </Box>
              <Box style={styles.date}>
                {getFormattedDate({ date: datePublished, noDay: true })}
              </Box>
              {endorser.external && (
                <Box style={styles.externalFeedback}>{t('review.card.external')}</Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box style={styles.splitterVertical} />

        <Box style={styles.contentWrap}>
          <Box style={styles.title}>{title}</Box>

          {photo && (
            <Box style={styles.imageWrap}>
              <ReviewImage photo={photo} {...reviewImageProps} />
            </Box>
          )}

          {message && (
            <Message
              onReadMoreClick={readMore}
              more={more}
              message={message}
              response={response}
              {...messageProps}
            />
          )}
        </Box>
      </Box>
    </BorderedContainer>
  );
};

export default Endorser;
